import React from "react";
import { motion } from "framer-motion";
import { AnimatedNumber } from "@/components/common/Widgets";
import { fmtNum, signPct } from "@/lib/format";

// Semi-circular instrument gauge: needle driven by coal variance %.
export function IntelligenceGauge({ expected, actual, variancePct, status }) {
  const W = 420, H = 250, cx = W / 2, cy = 210, r = 165;

  // Map variance -6% .. +20% across the 180deg arc (left -> right).
  const minV = -6, maxV = 20;
  const clamp = (v) => Math.max(minV, Math.min(maxV, v));
  const vToAngle = (v) => 180 - ((clamp(v) - minV) / (maxV - minV)) * 180; // deg
  const polar = (angleDeg, radius) => {
    const a = (angleDeg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(a), y: cy - radius * Math.sin(a) };
  };
  const arc = (a0, a1, radius) => {
    const p0 = polar(a0, radius), p1 = polar(a1, radius);
    const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
    const sweep = a1 < a0 ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${radius} ${radius} 0 ${large} ${sweep} ${p1.x} ${p1.y}`;
  };

  // Zone boundaries (in variance %): green up to 4, yellow 4-10, red 10+
  const segs = [
    { from: minV, to: 4, color: "#33b76d" },
    { from: 4, to: 10, color: "#fab20b" },
    { from: 10, to: maxV, color: "#E63946" },
  ];
  const needleAngle = vToAngle(variancePct ?? 0);
  const needleTip = polar(needleAngle, r - 26);
  const statusColors = { critical: "#E63946", warning: "#fab20b", nominal: "#33b76d" };
  const sc = statusColors[status] || "#33b76d";

  return (
    <div className="relative w-full flex flex-col items-center" data-testid="intelligence-gauge">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[460px]">
        {/* track */}
        <path d={arc(180, 0, r)} fill="none" stroke="#16223d" strokeWidth="22" strokeLinecap="butt" />
        {/* colored zones */}
        {segs.map((s, i) => (
          <path key={i} d={arc(vToAngle(s.from), vToAngle(s.to), r)} fill="none"
            stroke={s.color} strokeWidth="22" strokeLinecap="butt" opacity="0.92" />
        ))}
        {/* ticks */}
        {Array.from({ length: 14 }).map((_, i) => {
          const ang = 180 - (i / 13) * 180;
          const a = (ang * Math.PI) / 180;
          const o = r - 13, inn = r - 2;
          return (
            <line key={i} x1={cx + inn * Math.cos(a)} y1={cy - inn * Math.sin(a)}
              x2={cx + o * Math.cos(a)} y2={cy - o * Math.sin(a)}
              stroke="rgba(255,255,255,0.25)" strokeWidth="1.4" />
          );
        })}
        {/* needle */}
        <motion.line
          x1={cx} y1={cy} x2={needleTip.x} y2={needleTip.y}
          stroke={sc} strokeWidth="3.5" strokeLinecap="round"
          initial={false}
          animate={{ x2: needleTip.x, y2: needleTip.y }}
          transition={{ type: "spring", stiffness: 90, damping: 13 }}
          style={{ filter: `drop-shadow(0 0 6px ${sc}aa)` }}
        />
        <circle cx={cx} cy={cy} r="9" fill={sc} />
        <circle cx={cx} cy={cy} r="15" fill="none" stroke={sc} strokeWidth="1.5" opacity="0.4" />
        {/* zone labels */}
        <text x={polar(165, r + 16).x} y={polar(165, r + 16).y} fill="#33b76d" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">ON TARGET</text>
        <text x={cx} y={28} fill="#fab20b" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">OVER</text>
        <text x={polar(15, r + 18).x} y={polar(15, r + 18).y} fill="#E63946" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">CRITICAL</text>
      </svg>

      {/* center variance read-out */}
      <div className="absolute" style={{ top: 118, left: 0, right: 0, textAlign: "center" }}>
        <div className="eyebrow">Coal Variance</div>
        <div className="text-4xl font-bold tnum mt-0.5" style={{ color: sc }}>{signPct(variancePct)}</div>
      </div>

      {/* expected vs actual */}
      <div className="grid grid-cols-2 gap-3 w-full max-w-[420px] -mt-4">
        <div className="panel-2 px-4 py-3 text-center">
          <div className="eyebrow">Expected Coal</div>
          <div className="text-xl font-bold tnum text-ink mt-1">
            <AnimatedNumber value={expected} decimals={1} /> <span className="text-xs text-ink-soft">MT</span>
          </div>
        </div>
        <div className="panel-2 px-4 py-3 text-center" style={{ borderColor: `${sc}55` }}>
          <div className="eyebrow">Actual Coal</div>
          <div className="text-xl font-bold tnum mt-1" style={{ color: sc }}>
            <AnimatedNumber value={actual} decimals={1} /> <span className="text-xs text-ink-soft">MT</span>
          </div>
        </div>
      </div>
    </div>
  );
}
